import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { Check, Reply } from 'lucide-react';

import SectionTabs from '@/components/SectionTabs';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ADVANCED_CREATORS } from '@/constants';
import { DocumentContext } from '@/contexts/DocumentContext';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { DETAIL_MODE, DETAIL_TYPE } from '@/hooks/useDetailMode';
import { useNotificationModal } from '@/hooks/useNotificationModal';
import { useTemplateById } from '@/hooks/useTemplatesAPI';
import PageLayout from '@/layouts/PageLayout';
import { apiService } from '@/lib/axios';

const TemplatePreviewPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const { load, reset, template, error } = useTemplateById();

  const [activeTab, setActiveTab] = useState('information');
  const [formData, setFormData] = useState({});

  const { isAdminOrSuperAdmin, groups } = useAdminAccess();
  const canUpdateTemplate = isAdminOrSuperAdmin || groups.includes('QA');
  const { showConfirmModal } = useConfirmModal();
  const navigate = useNavigate();
  const { showNotificationModal } = useNotificationModal();

  const canEditName = useMemo(
    () =>
      selectedTemplate && !ADVANCED_CREATORS.includes(selectedTemplate.creator),
    [selectedTemplate]
  );

  const updateTemplate = async () => {
    try {
      const updateData = {
        name: selectedTemplate.name,
        config: selectedTemplate.config,
        values: selectedTemplate.values,
      };

      const response = await apiService.templates.update(
        selectedTemplate.id,
        updateData
      );

      if (response.data.success) {
        const savedTemplate = {
          id: response.data.data.id,
          type: response.data.data.type,
          name: response.data.data.name,
          creator: response.data.data.creator,
          default: response.data.data.isDefault,
          canDelete: response.data.data.canDelete,
          config: response.data.data.config,
          values: response.data.data.values,
        };

        // Update the selected template with saved data
        setSelectedTemplate(savedTemplate);
        // Reload templates to reflect changes in the list
        reset();

        showNotificationModal(
          'Template Published',
          `Template "${selectedTemplate.name}" has been published successfully!`,
          'success'
        );

        navigate('/templates');
        setSelectedTemplate(null);
      } else {
        throw new Error(response.data.error || 'Failed to publish template');
      }
    } catch (error) {
      console.error('Error publishing template:', error);
      showNotificationModal(
        'Publish Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to publish template',
        'error'
      );
    }
  };

  const handleBack = () => {
    navigate('/templates');
    setSelectedTemplate(null);
  };

  const handlePublish = async () => {
    // Validate template before showing confirmation
    if (!selectedTemplate.name || !selectedTemplate.name.trim()) {
      showNotificationModal(
        'Validation Error',
        'Template name is required for publishing',
        'error'
      );
      return;
    }

    showConfirmModal({
      title: 'Publish Template',
      description: `Are you sure you want to publish the template "${selectedTemplate.name}"? This will save all changes and make it available for use.`,
      onConfirm: updateTemplate,
    });
  };

  useEffect(() => {
    if (id) load(id);
  }, [id, load]);

  useEffect(() => {
    const draft = location.state?.templateDraft;
    setSelectedTemplate(template && draft?.id === template.id
      ? { ...template, name: draft.name, config: draft.config, values: draft.values }
      : template);
  }, [template, location.state]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen text-destructive">
        Error loading template: {error}
      </div>
    );
  }

  if (!template?.id) return <>Loading...</>;

  if (!selectedTemplate) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">No template selected</p>
      </div>
    );
  }

  return (
    <DocumentContext.Provider
      value={{ form: formData, id, isPreview: false, mode: DETAIL_MODE.EDIT }}
    >
      <PageLayout
        title={`Document Preview: ${selectedTemplate.name}`}
        toolbarHead={
          <>
            <Button variant="outline" onClick={handleBack}>
              <Reply className="mr-2 h-4 w-4" />
              Back to Templates
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/template/${selectedTemplate.id}`, {
                state: { templateDraft: selectedTemplate },
              })}
            >
              <Reply className="w-4 h-4 mr-2" />
              Back to Template View
            </Button>
          </>
        }
        toolbarTail={
          <>
            {canEditName && canUpdateTemplate && !selectedTemplate.isUsed && (
              <Button onClick={handlePublish}>
                Publish
                <Check className="w-4 h-4 mr-2" />
              </Button>
            )}
          </>
        }
      >
        <Card>
          <CardHeader>
            <CardTitle>Document Structure</CardTitle>
            <CardDescription>
              Visual representation of the document based on your template
              configuration
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <SectionTabs
              activeTab={activeTab}
              detailType={DETAIL_TYPE.TEMPLATE}
              setActiveTab={setActiveTab}
              formData={formData}
              setFormData={setFormData}
              template={selectedTemplate}
            />
          </CardContent>
        </Card>
      </PageLayout>
    </DocumentContext.Provider>
  );
};

export default TemplatePreviewPage;
